import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";

import { HseDetailsNotice } from "components/ui";
import { Notice, NoticeText } from "components/ui/Notice";
import { useHealthlinkPractice } from "features/healthlink/hooks/useHealthlinkPractice";
import {
  canPatientReachHse,
  HSE_FIELD,
  HSE_REQUIRED_FIELDS,
} from "features/healthlink/utils/hsePatientReadiness";
import { toAdministrationMethodCode } from "features/immunizations/constants/administrationMethods";
import { useHealthlinkVaccines } from "features/immunizations/hooks/useHealthlinkVaccines";
import { usePatientImmunisationHistory } from "features/immunizations/hooks/usePatientImmunisationHistory";
import { buildAdministeredElsewhereFields } from "features/immunizations/utils/administeredElsewhere";
import { applyHealthcareWorkerRiskCode } from "features/immunizations/utils/healthcareWorkerRiskCode";
import {
  isOutbreakProgramme,
  toProgrammeCode,
} from "features/immunizations/utils/vaccinationProgramme";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { useAllStaffMembers } from "features/practice/hooks/useAllStaffMembers";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { getServerErrorMessage } from "utils/showMutationError";

import style from "./AddVaccineForm.module.css";
import {
  AdministrationSection,
  FormActions,
  HealthcareWorkerField,
  HseEntitlementNotice,
  NoPpsnClaimNotice,
  ReactionSection,
  RiskFactorsSection,
  TextAreaField,
  VACCINE_MODE,
  VaccineDetailsSection,
  VaccineSelectionSection,
} from "./components";
import { VaccineValidationsModal } from "./components/VaccineValidationsModal/VaccineValidationsModal";
import { RSV_VACCINE_TYPE } from "./constants";
import { useVaccineForm, useVaccineFormMutation } from "./hooks";
import {
  isHealthcareWorkerQuestionAsked,
  isIndicationRequired,
} from "./utils/healthcareWorkerQuestion";

export const AddVaccineForm = ({
  currentPatientId,
  closeModal,
  tenantVal,
  vaccine,
  showPatientSelect = false,
  defaultMode,
  healthlinkEnabled = true,
  // Opened from a due dose on the schedule: the product where the course has exactly one,
  // and the dose number always (KIN-1842).
  prefill,
}) => {
  /*
   * `healthlinkEnabled` is false until the practice settings and the HealthLink
   * certificate have both loaded, and this runs once, at mount — so keying the initial
   * mode on it opened every form in Manual on a cold load, and only landed on
   * HealthLink when those queries happened to be cached already. An edit of a manual
   * record is the one case that genuinely starts in Manual and stays there.
   *
   * The unavailable cases are handled after the fact, by the effect below, once their
   * data has actually settled.
   */
  const isEditOfManualRecord = !!vaccine?.id && !vaccine?.vaccineCodeId;
  const [vaccineMode, setVaccineMode] = useState(
    isEditOfManualRecord
      ? VACCINE_MODE.MANUAL
      : defaultMode || VACCINE_MODE.HEALTHLINK
  );

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientError, setPatientError] = useState("");
  const [validationDialogState, setValidationDialogState] = useState({
    open: false,
    validations: [],
    serverPayload: null,
    submitPayload: null,
  });
  const [lastSubmitPayload, setLastSubmitPayload] = useState(null);

  const effectivePatientId = showPatientSelect
    ? selectedPatient?.id
    : currentPatientId;

  const handlePatientChange = (patient) => {
    setSelectedPatient(patient);
    if (patient) setPatientError("");
  };

  const { data: patientData, isLoading: isLoadingPatient } = usePatientData(
    showPatientSelect ? selectedPatient?.id : currentPatientId
  );
  // Resolved once: the age band behind the Healthcare Worker question needs it, and so
  // does the dose-interval history below.
  const resolvedPatientDateOfBirth =
    selectedPatient?.dateOfBirth ?? patientData?.dateOfBirth ?? null;

  const { data: doctors = [], isLoading: isLoadingDoctors } =
    useAllStaffMembers();
  const { data: healthlinkVaccines = [] } = useHealthlinkVaccines();
  // For the rules that depend on what the patient already has, rather than on the
  // product — PPV23's five-year window.
  const { data: immunisationHistory } =
    usePatientImmunisationHistory(effectivePatientId);
  const { data: healthlinkPractice, isLoading: isLoadingHealthlinkPractice } =
    useHealthlinkPractice();

  const {
    form,
    isEditMode,
    isDifferentDoctor,
    riskFactors,
    isLoadingRisks,
    isRiskFactorsError,
    riskFactorsError,
    selectedRiskCount,
    totalAvailableRisks,
    handleHealthlinkVaccineSelect,
    handleClearRiskFactors,
    vaccineBatches,
    isLoadingVaccineBatches,
    recordedBatchOption,
  } = useVaccineForm({
    vaccine: vaccine ?? (prefill?.doseNumberCode ? { doseNumberCode: prefill.doseNumberCode } : undefined),
    currentPatientId: effectivePatientId,
    patientDateOfBirth: resolvedPatientDateOfBirth,
  });

  const handleValidationError = useCallback(
    ({ validations, serverPayload, submitPayload }) => {
      setValidationDialogState({
        open: true,
        validations,
        serverPayload,
        submitPayload,
      });
    },
    []
  );

  const { mutate: saveVaccine, isPending } = useVaccineFormMutation({
    tenantVal,
    vaccineId: vaccine?.id,
    onSuccess: () => {
      form.reset();
      closeModal();
    },
    onValidationError: handleValidationError,
  });

  const handleModeChange = (newMode) => {
    setVaccineMode(newMode);

    if (newMode === VACCINE_MODE.MANUAL) {
      handleHealthlinkVaccineSelect(null);
    }
  };

  const buildSubmitPayload = (values) => {
    const defaultedSelectedCodes = riskFactors.defaultedRisks
      .filter((r) => r.selected)
      .map((r) => r.code);
    // React Hook Form rebuilds a checkbox group from the boxes on screen, so a code with
    // no box of its own — a defaulted one, or the healthcare-worker answer — is dropped
    // the moment any other box is ticked. Both are restored here, where the payload is
    // the last word on what is reported.
    const allRiskCodes = applyHealthcareWorkerRiskCode({
      currentCodes: [
        ...new Set([
          ...defaultedSelectedCodes,
          ...(values.riskFactorCodes || []),
        ]),
      ],
      isQuestionAsked: isHealthcareWorkerQuestionAsked({
        vaccineType: values.type,
        dateOfBirth: resolvedPatientDateOfBirth,
        dateGiven: values.dateGiven,
      }),
      healthcareWorker: values.healthcareWorker,
      isEditMode,
    });

    // `vaccinationProgramme` is the form's own value; the wire carries a code, and
    // Primary Childhood carries none at all — null there means "derive it from the
    // vaccine", which is what every existing record and every non-MMR record holds
    // (KIN-1875). A stray outbreak code on a dose that is not an Outbreak one puts a
    // segment on the message that must not be there, so it travels only with its
    // programme.
    // `givenElsewhere` and `givenElsewhereBy` are the form's own values. The wire has no
    // flag: `administeredElsewhereAt` being non-null is the marker, and the backend keys
    // every one of its guarantees off that (KIN-1883).
    const {
      vaccinationProgramme,
      outbreakCode,
      givenElsewhere,
      givenElsewhereBy,
      ...rest
    } = values;
    const programmeCode = toProgrammeCode(vaccinationProgramme);

    return {
      ...rest,
      patientId: effectivePatientId,
      riskFactorCodes: allRiskCodes,
      ...buildAdministeredElsewhereFields({
        isGivenElsewhere: givenElsewhere,
        givenElsewhereBy,
        dateGiven: values.dateGiven,
      }),
      ...(programmeCode ? { vaccinationProgrammeCode: programmeCode } : {}),
      ...(isOutbreakProgramme(vaccinationProgramme) && outbreakCode
        ? { outbreakCode }
        : {}),
    };
  };

  const handleSubmit = (values) => {
    if (showPatientSelect && !selectedPatient) {
      setPatientError("Please select a patient.");
      return;
    }

    // Belt and braces behind the disabled Save. Must carry the mode condition too: the
    // readiness check is about reaching NIIS, and a manual record never does — without
    // this, Save did nothing at all, silently, for a patient missing any of those
    // details, which is worse than the button being disabled.
    if (isHealthLinkMode && isBlockedByPatientDetails) return;

    const payload = buildSubmitPayload(values);

    setLastSubmitPayload(payload);
    saveVaccine(payload);
  };

  const effectiveSubmitPayload = useMemo(
    () => validationDialogState.submitPayload ?? lastSubmitPayload,
    [lastSubmitPayload, validationDialogState.submitPayload]
  );

  const handleValidationsContinue = useCallback(
    (mitigatedValidations) => {
      if (!effectiveSubmitPayload) return;
      setValidationDialogState((prev) => ({ ...prev, open: false }));
      const payload = {
        ...effectiveSubmitPayload,
        validations: mitigatedValidations,
      };
      setLastSubmitPayload(payload);
      saveVaccine(payload);
    },
    [effectiveSubmitPayload, saveVaccine]
  );

  // Drives the layout as well as the payload: with the administration fields hidden,
  // Vaccine details and Reaction have two fields each and share one row (KIN-1883).
  const isGivenElsewhere = form.watch("givenElsewhere");
  const watchedVaccineType = form.watch("type");
  const watchedVaccineCodeId = form.watch("vaccineCodeId");

  const isCovidType =
    typeof watchedVaccineType === "string" && watchedVaccineType === "COVID-19";
  const isPciType =
    typeof watchedVaccineType === "string" && watchedVaccineType === "PCI";
  // RSV carries no risk factors at all, so the section would only ever render
  // empty for it (KIN-1675).
  // The dose the selected product determines, or null when we hold none for it.
  //
  // Resolved from the catalogue by id rather than from the record, because the record
  // carries no dose reference and no SNOMED — only the product does. Works the same on
  // a new entry and on an edit, which is what the previous SNOMED-keyed version did
  // not: nothing writes a SNOMED into form state when an existing record is opened.
  //
  // A product that has since been withdrawn is not in this list, so its dose stays
  // editable — the honest outcome when no reference is at hand.
  const selectedProduct = healthlinkVaccines.find(
    (product) => String(product.id) === String(watchedVaccineCodeId)
  );
  const derivedDoseMl = selectedProduct?.doseMl ?? null;

  /*
   * The route the product is given by, as a table 0162 code (KIN-2115). Empty for the
   * products the catalogue states no route for — flu, COVID-19, Pneumovax and others —
   * and those keep a route the clinician picks for themselves.
   */
  const derivedRouteCode = toAdministrationMethodCode(
    selectedProduct?.routeOfAdministration
  );

  const isRsvType = watchedVaccineType === RSV_VACCINE_TYPE;

  // Phase 2 §5.3.1. Placed above the Indications, and gated on exactly what the backend
  // gates its own requirement on, so the form never asks a question the record does not
  // need — nor withholds one it does.
  const showHealthcareWorkerQuestion =
    vaccineMode === VACCINE_MODE.HEALTHLINK &&
    isHealthcareWorkerQuestionAsked({
      vaccineType: watchedVaccineType,
      dateOfBirth: resolvedPatientDateOfBirth,
      dateGiven: form.watch("dateGiven"),
    });

  const indicationRequired = isIndicationRequired({
    vaccineType: watchedVaccineType,
    dateOfBirth: resolvedPatientDateOfBirth,
    dateGiven: form.watch("dateGiven"),
    healthcareWorker: form.watch("healthcareWorker"),
  });

  const showRiskFactors =
    vaccineMode === VACCINE_MODE.HEALTHLINK &&
    !isRsvType &&
    (form.watch("vaccineCodeId") ||
      (isEditMode && (vaccine?.riskFactorCodes?.length > 0 || isPciType)));

  const hasPatientSelected = showPatientSelect
    ? selectedPatient !== null
    : true;
  // HealthLink is enabled in practice settings but no HealthLink practice is
  // configured for this tenant (GET /healthlink/practice → 404 → null). This is
  // a valid state — the surgery can still record vaccines manually — so we hide
  // HealthLink mode, force Manual, and show a warning instead of an error.
  const isHealthlinkPracticeMissing =
    healthlinkEnabled &&
    !isEditMode &&
    !isLoadingHealthlinkPractice &&
    !healthlinkPractice;
  const isHealthLinkMode = vaccineMode === VACCINE_MODE.HEALTHLINK;
  // What a NIIS submission needs from the patient record. The financial class is
  // excluded on an edit, matching the rule it replaces: an already submitted record is
  // not re-gated on a classification set since.
  //
  // Computed in both modes, not just HealthLink: the form stays open and Manual stays
  // usable — a record kept locally is never sent to NIIS, so none of this applies to it
  // — but the GP is told why HealthLink is unavailable rather than finding the tab inert.
  //
  // One list drives the notice, the mode switch and the disabled Save, so what a GP
  // reads and what they cannot press can never disagree.
  const healthlinkRequiredFields = useMemo(
    () => [
      ...HSE_REQUIRED_FIELDS,
      HSE_FIELD.country,
    ],
    []
  );
  const isPatientLoadedForCheck = hasPatientSelected && !isLoadingPatient;
  const isBlockedByPatientDetails =
    isPatientLoadedForCheck &&
    !canPatientReachHse(patientData, healthlinkRequiredFields);

  /*
   * Only a missing HealthLink practice withdraws the mode: there is nothing for the GP to fix on
   * this form, so leaving them in a mode that cannot work is a dead end.
   *
   * An incomplete patient record is not that. It is fixable, the form says what is missing, and
   * Save is withheld until it is — so the GP stays where they are rather than being moved to
   * Manual and left to work out what happened.
   *
   * Keyed on knowing the practice is missing, not on HealthLink merely not being usable yet: the
   * latter is also true while the practice settings and the certificate are still loading, which
   * once forced Manual on the first render of every form and never switched back.
   */
  const shouldWithdrawHealthlink = isHealthlinkPracticeMissing;

  useEffect(() => {
    if (shouldWithdrawHealthlink && vaccineMode === VACCINE_MODE.HEALTHLINK) {
      setVaccineMode(VACCINE_MODE.MANUAL);
      handleHealthlinkVaccineSelect(null);
    }
  }, [shouldWithdrawHealthlink, vaccineMode, handleHealthlinkVaccineSelect]);

  /*
   * A catalogue vaccination cannot be submitted without the spec version the chosen
   * X0069/X0064 codes belong to — the backend refuses it (@SpecVersionRequired), and
   * the refusal used to arrive as "Error count: 1" after the whole form was filled in
   * (KIN-1744).
   *
   * The value arrives with the risk factors, so it is briefly absent while that query
   * runs and permanently absent if the query failed. Disabling Save covers both without
   * the form having to tell them apart, and a validation message was the wrong tool:
   * `specVersion` is not a rendered field, so an error on it would have had nowhere to
   * appear — Save would have done nothing, silently, which is worse than a disabled
   * button. The failed case gets the notice above; the loading case resolves itself.
   */
  const isAwaitingSpecVersion =
    isHealthLinkMode && !!form.watch("vaccineCodeId") && !form.watch("specVersion");
  /*
   * The same condition, once the query has settled — a disabled Save with nothing
   * explaining it is worse than the unreadable 400 it replaced, and it would be a
   * permanent dead end whenever the endpoint answers without a version rather than
   * failing outright. Keyed on the missing value rather than on the request failing, so
   * both causes are covered ("No specVersion causes the save to fail completely",
   * KIN-1744).
   */
  const cannotResolveSpecVersion = isAwaitingSpecVersion && !isLoadingRisks;
  /*
   * The server's own reason when it gave one. An unknown vaccineType is answered with a
   * 400 naming the value and the accepted ones (KIN-1807), and "reload the page and try
   * again" is actively wrong advice for it — reloading fails identically every time. The
   * generic line stays as the fallback for a failure that carries no reason.
   */
  const specVersionFailureReason = getServerErrorMessage(
    riskFactorsError,
    "The vaccination rules for this product could not be loaded, so this record cannot be submitted yet. Reload the page and try again."
  );
  const isSubmitDisabled =
    (isHealthLinkMode && isBlockedByPatientDetails) ||
    isAwaitingSpecVersion ||
    isDifferentDoctor ||
    isPending;

  return (
    <div>
      <FormProvider {...form}>
        {isDifferentDoctor && (
          <Notice>
            <NoticeText>
              This vaccine was administered by a different doctor. Only the
              original vaccinator can edit this record.
            </NoticeText>
          </Notice>
        )}
        <form
          className={style.addPregnancyForm}
          onSubmit={form.handleSubmit(handleSubmit, onInvalidSubmit("AddVaccineForm"))}
        >
          {/* Above the form rather than beside the save button: a GP should learn that
              nothing can be submitted for this patient before filling it in, not after
              (KIN-1733). One notice for every missing detail — this form used to stack
              three differently-styled boxes saying the same kind of thing. */}
          {!!cannotResolveSpecVersion && (
            <Notice>
              <NoticeText>
                {isRiskFactorsError
                  ? specVersionFailureReason
                  : "The vaccination rules for this product did not include a specification version, so this record cannot be submitted. Reload the page, and report it if it keeps happening."}
              </NoticeText>
            </Notice>
          )}

          {/* HealthLink mode only. A manual record is never sent anywhere, so a notice saying
              nothing can reach NIIS is answering a question the GP did not ask — and the reason
              HealthLink is unavailable is now on the greyed tab itself. */}
          {!!isPatientLoadedForCheck && !!isHealthLinkMode && (
            <HseDetailsNotice
              patient={patientData}
              patientId={effectivePatientId}
              requiredFields={healthlinkRequiredFields}
              submissionName="as a Healthlink vaccine"
            />
          )}

          {/* Below the blocking notice and only in HealthLink mode: a manual record is
              never claimed for, so the PPSN is beside the point there. */}
          {!!isPatientLoadedForCheck && !!isHealthLinkMode && (
            <NoPpsnClaimNotice
              patient={patientData}
              patientId={effectivePatientId}
            />
          )}

          {/* Same placement and for the same reason: it bears on this submission, not on the
              patient's history, and only where a HealthLink message is actually being built. */}
          {!!isPatientLoadedForCheck && !!isHealthLinkMode && (
            <HseEntitlementNotice
              patient={patientData}
              patientId={effectivePatientId}
            />
          )}

          <VaccineSelectionSection
            healthlinkVaccines={healthlinkVaccines}
            onVaccineSelect={handleHealthlinkVaccineSelect}
            prefillVaccineCodeId={prefill?.vaccineCodeId}
            mode={vaccineMode}
            onModeChange={handleModeChange}
            healthlinkEnabled={healthlinkEnabled}
            showPatientSelect={showPatientSelect}
            selectedPatient={selectedPatient}
            onPatientChange={handlePatientChange}
            patientError={patientError}
            isCovidType={isCovidType}
            isEditMode={isEditMode}
            patientDateOfBirth={resolvedPatientDateOfBirth}
            immunisationHistory={immunisationHistory}
            editedImmunisationId={vaccine?.id}
            patientId={effectivePatientId}
          />

          {!!showHealthcareWorkerQuestion && <HealthcareWorkerField />}

          {showRiskFactors && (
            <RiskFactorsSection
              riskFactors={riskFactors}
              isEditMode={isEditMode}
              isLoading={isLoadingRisks}
              selectedCount={selectedRiskCount}
              totalAvailable={totalAvailableRisks}
              onClear={handleClearRiskFactors}
              existingRiskCodes={vaccine?.riskFactorCodes}
              isIndicationRequired={indicationRequired}
            />
          )}

          <AdministrationSection
            derivedRouteCode={derivedRouteCode}
            doctors={doctors}
            isLoadingDoctors={isLoadingDoctors}
            mode={vaccineMode}
            isEditMode={isEditMode}
            fallbackVaccinatorName={
              vaccine?.administrationStaff
                ? `${vaccine.administrationStaff.firstName} ${vaccine.administrationStaff.lastName}`.trim()
                : undefined
            }
          />

          <VaccineDetailsSection
            mode={vaccineMode}
            derivedDoseMl={derivedDoseMl}
            batches={vaccineBatches}
            isLoadingBatches={isLoadingVaccineBatches}
            recordedBatch={recordedBatchOption}
          >
            {!!isGivenElsewhere && <ReactionSection unwrapped />}
          </VaccineDetailsSection>

          {!isGivenElsewhere && <ReactionSection />}

          <TextAreaField name="notes" label="Notes" maxLength={999} />

          <FormActions
            onCancel={closeModal}
            isSubmitting={isPending}
            isSubmitDisabled={isSubmitDisabled}
          />
        </form>
      </FormProvider>

      <VaccineValidationsModal
        open={validationDialogState.open}
        onClose={() =>
          setValidationDialogState((prev) => ({
            ...prev,
            open: false,
          }))
        }
        validations={validationDialogState.validations}
        isSubmitting={isPending}
        onContinue={handleValidationsContinue}
      />
    </div>
  );
};
